import { maybe, Maybe, rd } from '@passionware/monads';
import { User } from 'lucide-react';
import { ReactNode } from 'react';
import {
  FilterConfigBase,
  FilterEnumConfig
} from 'v5/patterns/filters/FilterWidget.types';
import { MergeServices } from 'v5/platform/ts/services';
import {
  FilterMetastructureRequest,
  useMetastructureFilters
} from 'v5/features/list-filters/useMetastructureFilters';
import { WithMetastructureMappingService } from 'v5/services/internal/MetastructureMappingService/MetastructureMappingService';
import { WithProductionTypeService } from 'v5/services/internal/ProductionTypeService/ProductionTypeService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { createOwnerFilterConfig } from '../list-filters/ownerFilterConfig';
import { WithGroupsService } from 'v5/services/io/GroupsService/GroupsService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { WithTagService } from 'v5/services/io/TagsService/TagsService';
import { WithProductionService } from 'v5/services/io/ProductionService/ProductionService';
import { createProductionStatusFilterConfig } from '../list-filters/metastructure-filters/core/production-status';

/**
 * Creates a filter config for production type selection
 */
function createProductionTypeFilterConfig(
  services: MergeServices<[WithProductionTypeService]>
): FilterEnumConfig<number> {
  const useProductionTypeOptions = (query: string) => {
    const productionTypes = services.productionTypeService.useProductionTypes();

    return rd.map(productionTypes, types => {
      const filtered = types.filter(
        type => !query || type.name?.toLowerCase().includes(query.toLowerCase())
      );
      return filtered.map(type => ({
        value: type.id,
        label: type.name || `Type ${type.id}`
      }));
    });
  };

  const useProductionTypeValues = (values: number[]) => {
    const productionTypes = services.productionTypeService.useProductionTypes();

    return rd.map(productionTypes, types => {
      return values
        .map(id => types.find(type => type.id === id))
        .filter(maybe.isPresent)
        .map(type => ({
          value: type.id,
          label: type.name || `Type ${type.id}`
        }));
    });
  };

  return {
    label: 'Production Type',
    icon: <User className="size-4" />,
    type: 'enum',
    createDefaultValue: () => ({ operator: 'matchAny', value: [] }),
    options: {
      useValues: useProductionTypeValues,
      useOptions: useProductionTypeOptions
    }
  };
}

export function useProductionFilters(
  metastructureRequest: Maybe<FilterMetastructureRequest>,
  services: MergeServices<
    [
      WithMetastructureMappingService,
      WithResourceTypeService,
      WithResourceService,
      WithTagService,
      WithGroupsService,
      WithProductionService,
      WithProductionTypeService
    ]
  >,
  scopedProductionTypeId?: number
) {
  // Get the base metastructure filters (core + custom fields)
  const baseFilters = useMetastructureFilters(metastructureRequest, services);

  // Define production-specific filters that aren't handled by metastructure
  const productionSpecificConfigs: Record<string, FilterConfigBase> = {
    // Only include production type filter when not scoped to a specific type
    ...(scopedProductionTypeId === undefined && {
      productionTypeId: createProductionTypeFilterConfig(services)
    }),
    status: createProductionStatusFilterConfig(),
    userId: createOwnerFilterConfig()
  };

  const productionSpecificChildren = Object.keys(productionSpecificConfigs);

  // Combine base filters with production-specific filters
  return rd.useMemoMapMonadic(baseFilters, baseResult => {
    const combinedConfigs = {
      ...productionSpecificConfigs,
      ...baseResult.configs
    };

    const combinedGroups: Array<{
      name: string;
      icon: ReactNode;
      children: string[];
    }> = [];

    // Add production-specific fields group
    if (productionSpecificChildren.length > 0) {
      combinedGroups.push({
        name: 'Production Specific',
        icon: <User className="size-4" />,
        children: productionSpecificChildren
      });
    }

    // Add core fields group (from base filters)
    const coreGroup = baseResult.groups.find(g => g.name === 'Core Fields');
    if (coreGroup) {
      combinedGroups.push(coreGroup);
    }

    // Add custom field groups (from base filters)
    const customGroups = baseResult.groups.filter(
      g => g.name !== 'Core Fields'
    );
    combinedGroups.push(...customGroups);

    const combinedOrder = combinedGroups.flatMap(group => group.children);

    return rd.of({
      configs: combinedConfigs,
      groups: combinedGroups,
      order: combinedOrder
    });
  });
}
